import type {
  StorageAccessPolicy,
  StorageBlobHttpHeaders,
  StorageOperationError,
  StoragePolicyResourceKind,
  StorageResourceRef,
} from '../../types/azureStorage';
import { normalizeSasPermissions } from './azureStorageSasParameters';

const problem = (code: string, message: string): StorageOperationError => ({
  category: 'invalid_input', code, retryable: false, message,
});

/**
 * "__proto__" is a valid Azure metadata name and tag key, but Electron's context bridge rebuilds every object crossing
 * between the app window and main by property assignment, which drops that key in both directions (probed 2026-09-24,
 * review D RD1). A save that included it would silently lose it, so it is refused where it is typed.
 */
const UNCARRIED_KEY = '__proto__';

export function metadataProblem(metadata: Record<string, string>): StorageOperationError | undefined {
  const folded = new Set<string>();
  let size = 0;
  for (const [key, value] of Object.entries(metadata)) {
    if (key === UNCARRIED_KEY) {
      return problem('UnsupportedMetadataName', 'Jam SQL Studio cannot save a metadata name "__proto__" (the app cannot pass that name between its window and the storage connection). Use another name.');
    }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/u.test(key)) {
      return problem('InvalidMetadataKey', 'Metadata keys must start with a letter or underscore and contain only letters, numbers and underscores.');
    }
    if (!/^[\x00-\x7F]*$/u.test(value)) return problem('InvalidMetadataValue', 'Metadata values must contain ASCII characters only.');
    const normalized = key.toLowerCase();
    if (folded.has(normalized)) return problem('DuplicateMetadataKey', 'Metadata keys must be unique without regard to letter case.');
    folded.add(normalized);
    size += key.length + value.length;
  }
  return size > 8 * 1024 ? problem('MetadataTooLarge', 'Metadata keys and values together cannot exceed 8 KiB.') : undefined;
}

export function blobTagsProblem(tags: Record<string, string>): StorageOperationError | undefined {
  const entries = Object.entries(tags);
  const allowed = /^[A-Za-z0-9 +./:=_-]*$/u;
  if (entries.length > 10) return problem('TooManyBlobTags', 'A blob can have at most 10 index tags.');
  for (const [key, value] of entries) {
    if (key === UNCARRIED_KEY) {
      return problem('UnsupportedBlobTagKey', 'Jam SQL Studio cannot save an index tag key "__proto__" (the app cannot pass that key between its window and the storage connection). Use another key.');
    }
    if (key.length < 1 || key.length > 128) return problem('InvalidBlobTagKey', 'Blob tag keys must contain between 1 and 128 characters.');
    if (!allowed.test(key)) return problem('InvalidBlobTagKey', 'Blob tag keys contain a character that Azure Blob Storage does not allow.');
    if (value.length > 256) return problem('InvalidBlobTagValue', 'Blob tag values cannot exceed 256 characters.');
    if (!allowed.test(value)) return problem('InvalidBlobTagValue', 'Blob tag values contain a character that Azure Blob Storage does not allow.');
  }
  return undefined;
}

function validMd5(value: string): boolean {
  if (!/^(?:[A-Za-z0-9+/]{4}){5}(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)$/u.test(value)) return false;
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
  return (value.length / 4) * 3 - padding === 16;
}

export function httpHeaderProblem(headers: StorageBlobHttpHeaders): StorageOperationError | undefined {
  for (const [name, value] of Object.entries(headers)) {
    if (value !== undefined && /[\r\n]/u.test(value)) return problem('InvalidHttpHeader', `The ${name} header cannot contain carriage returns or line feeds.`);
  }
  return headers.contentMD5 !== undefined && !validMd5(headers.contentMD5)
    ? problem('InvalidContentMD5', 'Content MD5 must be a base64-encoded 16-byte digest.')
    : undefined;
}

function validDate(value: string | undefined): boolean {
  return value === undefined || Number.isFinite(Date.parse(value));
}

/** Azure's five-identifier limit and canonical letters apply per policy-bearing resource (table `raud`, queue `raup`). */
export function accessPolicyProblem(policies: StorageAccessPolicy[], kind: StoragePolicyResourceKind = 'container'): StorageOperationError | undefined {
  if (policies.length > 5) return problem('TooManyAccessPolicies', `A ${kind} can have at most five stored access policies.`);
  const ids = new Set<string>();
  for (const policy of policies) {
    if (!policy.id.trim()) return problem('InvalidAccessPolicyId', 'Each stored access policy must have a non-empty identifier.');
    if (ids.has(policy.id)) return problem('DuplicateAccessPolicyId', 'Stored access policy identifiers must be unique.');
    ids.add(policy.id);
    if (policy.permissions !== undefined) {
      const normalized = normalizeSasPermissions(kind, policy.permissions);
      if (!normalized.ok || normalized.value !== policy.permissions) {
        return problem('NonCanonicalAccessPolicyPermissions', 'Stored access policy permissions must use valid letters in canonical order.');
      }
    }
    if (!validDate(policy.startsOn) || !validDate(policy.expiresOn)) {
      return problem('InvalidAccessPolicyDate', 'Stored access policy dates must be valid date and time values.');
    }
    if (policy.startsOn && policy.expiresOn && Date.parse(policy.startsOn) >= Date.parse(policy.expiresOn)) {
      return problem('AccessPolicyStartAfterExpiry', 'A stored access policy start must be earlier than its expiry.');
    }
  }
  return undefined;
}

/** Azure keeps an archived blob's metadata and HTTP headers read-only (index tags stay writable; Fable N3). */
export const ARCHIVED_BLOB_WRITE_REASON = "An archived blob's metadata and HTTP headers are read-only. Rehydrate it (Change tier…) to edit them.";

export function historicalBlobRefProblem(ref: StorageResourceRef): StorageOperationError | undefined {
  return ref.kind === 'blob' && (ref.snapshot !== undefined || ref.versionId !== undefined)
    ? problem('HistoricalBlobImmutable', 'Snapshots and versions are immutable; edit the current blob instead.')
    : undefined;
}
